import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import type { SupportThreadRow } from "@redipos/contracts";
import { soporteApi } from "../api.js";
import { playSupportSound } from "../lib/supportAudio.js";
import {
  ADMIN_SUPPORT_ACTIVE_THREAD,
  ADMIN_SUPPORT_SSE_MESSAGE,
  ADMIN_SUPPORT_SSE_READ,
  ADMIN_SUPPORT_SSE_TYPING,
  ADMIN_SUPPORT_THREADS_SYNC,
} from "../lib/supportRealtimeEvents.js";

export interface SupportBadgeState {
  total: number;
  unreadByTenant: Record<string, number>;
  connected: boolean;
  syncFromThreads: (threads: SupportThreadRow[]) => void;
}

const SupportBadgeContext = createContext<SupportBadgeState | null>(null);

function dispatchWindowEvent(name: string, detail?: unknown): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function threadsToUnreadMap(threads: SupportThreadRow[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const t of threads) {
    if (t?.id && (t.unread ?? 0) > 0) {
      map[t.id] = t.unread ?? 0;
    }
  }
  return map;
}

export function SupportBadgeProvider({ children }: { children: React.ReactNode }) {
  const [unreadByTenant, setUnreadByTenant] = useState<Record<string, number>>({});
  const [connected, setConnected] = useState(false);

  const activeThreadIdRef = useRef<string | null>(null);
  const onSupportPageRef = useRef(false);
  const seenMessageIdsRef = useRef<Set<string>>(new Set());

  const syncFromThreads = useCallback((threads: SupportThreadRow[]) => {
    setUnreadByTenant(threadsToUnreadMap(threads));
    for (const t of threads) {
      if (t?.id && (t.unread ?? 0) === 0) {
        seenMessageIdsRef.current.forEach((id) => {
          if (id.startsWith(`${t.id}:`)) seenMessageIdsRef.current.delete(id);
        });
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const onActiveThread = (event: Event) => {
      const detail = (event as CustomEvent<{ tenantId?: string | null }>).detail;
      const next =
        detail && typeof detail.tenantId === "string" ? detail.tenantId : null;
      activeThreadIdRef.current = next;
      if (next) onSupportPageRef.current = true;
    };

    const onThreadsSync = (event: Event) => {
      const detail = (event as CustomEvent<{ threads?: SupportThreadRow[] }>).detail;
      if (Array.isArray(detail?.threads)) {
        syncFromThreads(detail.threads);
      }
    };

    window.addEventListener(ADMIN_SUPPORT_ACTIVE_THREAD, onActiveThread as EventListener);
    window.addEventListener(ADMIN_SUPPORT_THREADS_SYNC, onThreadsSync as EventListener);

    return () => {
      window.removeEventListener(ADMIN_SUPPORT_ACTIVE_THREAD, onActiveThread as EventListener);
      window.removeEventListener(ADMIN_SUPPORT_THREADS_SYNC, onThreadsSync as EventListener);
    };
  }, [syncFromThreads]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const update = () => {
      const path = window.location.pathname || "";
      const onSupport = path === "/soporte" || path.startsWith("/soporte/");
      onSupportPageRef.current = onSupport;
      if (!onSupport) activeThreadIdRef.current = null;
    };
    update();
    window.addEventListener("popstate", update);
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      const r = origPush.apply(this, args as Parameters<typeof origPush>);
      update();
      return r;
    };
    window.history.replaceState = function (...args) {
      const r = origReplace.apply(this, args as Parameters<typeof origReplace>);
      update();
      return r;
    };
    return () => {
      window.removeEventListener("popstate", update);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    soporteApi
      .threads()
      .then((threads) => {
        if (cancelled || !Array.isArray(threads)) return;
        syncFromThreads(threads);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [syncFromThreads]);

  useEffect(() => {
    const disconnect = soporteApi.connectStream(
      (msg) => {
        if (!msg?.tenantId) return;

        dispatchWindowEvent(ADMIN_SUPPORT_SSE_MESSAGE, { message: msg });

        if (msg.isAdmin) return;
        if (msg.read) return;
        if (msg.id && seenMessageIdsRef.current.has(msg.id)) return;
        if (msg.id) seenMessageIdsRef.current.add(msg.id);

        const viewingThisThread =
          onSupportPageRef.current && activeThreadIdRef.current === msg.tenantId;

        if (!viewingThisThread) {
          setUnreadByTenant((prev) => {
            const current = prev[msg.tenantId] || 0;
            return { ...prev, [msg.tenantId]: current + 1 };
          });
        }

        playSupportSound(viewingThisThread ? "add" : "new", {
          onlyIfHidden: viewingThisThread,
        });
      },
      (typing) => {
        dispatchWindowEvent(ADMIN_SUPPORT_SSE_TYPING, typing);
      },
      () => setConnected(true),
      (payload) => {
        const tenantId = payload?.tenantId;
        if (!tenantId) return;
        setUnreadByTenant((prev) => {
          if (!(tenantId in prev) || prev[tenantId] === 0) return prev;
          const next = { ...prev };
          delete next[tenantId];
          return next;
        });
        dispatchWindowEvent(ADMIN_SUPPORT_SSE_READ, { tenantId });
      },
      () => setConnected(false),
    );
    return disconnect;
  }, []);

  const value = useMemo<SupportBadgeState>(() => {
    const total = Object.values(unreadByTenant).reduce((acc, n) => acc + n, 0);
    return { total, unreadByTenant, connected, syncFromThreads };
  }, [unreadByTenant, connected, syncFromThreads]);

  return (
    <SupportBadgeContext.Provider value={value}>
      {children}
    </SupportBadgeContext.Provider>
  );
}

export function useSupportBadge(): SupportBadgeState {
  const ctx = useContext(SupportBadgeContext);
  if (!ctx) {
    return {
      total: 0,
      unreadByTenant: {},
      connected: false,
      syncFromThreads: () => undefined,
    };
  }
  return ctx;
}
